// Clock sync (SPEC §4): estimate the offset between the local clock and the server's world clock
// with an NTP-style exchange, then map world bars to audio-engine samples so bar starts are
// audible at `epoch + n × 1.92 s`.
//
// One exchange: the client notes t0 (send) and t1 (receive) on its monotonic clock and the server
// answers with its time ts. Assuming symmetric paths, offset = ts − (t0 + t1)/2 and the error is at
// most rtt/2. We take several samples, keep the lowest-latency ones and use their median offset.

export interface SyncSample {
  /** Local time the request was sent (ms, same clock as t1). */
  t0: number;
  /** Local time the response arrived. */
  t1: number;
  /** Server time in the response (epoch ms). */
  server: number;
}

export interface SyncEstimate {
  /** Add to local epoch time to get world (server) time, in ms. */
  offsetMs: number;
  /** Round-trip time of the best sample. */
  rttMs: number;
  /** Bound on the offset error: half the best round trip. */
  errorBoundMs: number;
  samples: number;
}

export function sampleOffset(s: SyncSample): number {
  return s.server - (s.t0 + s.t1) / 2;
}

export function median(xs: number[]): number {
  const v = [...xs].sort((a, b) => a - b);
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
}

/** Median offset of the `keep` lowest-latency samples. */
export function estimateOffset(samples: SyncSample[], keep = 3): SyncEstimate {
  if (samples.length === 0) throw new Error("no sync samples");
  const sorted = [...samples].sort((a, b) => a.t1 - a.t0 - (b.t1 - b.t0));
  const best = sorted.slice(0, Math.max(1, Math.min(keep, sorted.length)));
  const rtt = best[0].t1 - best[0].t0;
  return { offsetMs: median(best.map(sampleOffset)), rttMs: rtt, errorBoundMs: rtt / 2, samples: samples.length };
}

export type TimeFetcher = () => Promise<number>;

/**
 * Run `rounds` exchanges against `fetchServerTime` (resolves to server epoch ms) using `now`
 * (local epoch ms, e.g. `performance.timeOrigin + performance.now()`).
 */
export async function measureOffset(
  fetchServerTime: TimeFetcher,
  now: () => number,
  rounds = 8,
  keep = 3,
): Promise<SyncEstimate> {
  const samples: SyncSample[] = [];
  for (let i = 0; i < rounds; i++) {
    const t0 = now();
    const server = await fetchServerTime();
    const t1 = now();
    samples.push({ t0, t1, server });
  }
  return estimateOffset(samples, keep);
}

/**
 * Smooths successive estimates: small changes are blended (they're mostly jitter), large jumps
 * (a clock change, a sleeping laptop) are taken directly.
 */
export class OffsetTracker {
  offsetMs: number | null = null;
  constructor(
    private readonly blend = 0.3,
    private readonly jumpMs = 50,
  ) {}

  update(e: SyncEstimate): number {
    if (this.offsetMs === null || Math.abs(e.offsetMs - this.offsetMs) > this.jumpMs) this.offsetMs = e.offsetMs;
    else this.offsetMs += (e.offsetMs - this.offsetMs) * this.blend;
    return this.offsetMs;
  }
}

/**
 * The engine sample at which world time `worldMs` must be *audible*, given a recent audio output
 * timestamp: engine sample `audibleSample` reaches the speakers at local performance time
 * `audiblePerfMs`, and `perfToWorld` converts performance time to world time (timeOrigin +
 * offset).
 */
export function engineSampleForWorldTime(
  worldMs: number,
  audible: { sample: number; perfMs: number },
  perfToWorldMs: (perfMs: number) => number,
  sampleRate: number,
): number {
  const worldAtAudible = perfToWorldMs(audible.perfMs);
  return audible.sample + ((worldMs - worldAtAudible) * sampleRate) / 1000;
}

/**
 * Drift correction for a bar boundary: the engine says bar `bar` starts at `engineStart`; it
 * should start at `wantStart`. Returns the nudge in samples (0 when within `toleranceSamples`),
 * or `relocate: true` when the error is too big for a nudge (> 100 ms).
 */
export function driftCorrection(
  wantStart: number,
  engineStart: number,
  sampleRate: number,
  toleranceSamples = 24,
): { nudge: number; relocate: boolean } {
  const err = Math.round(wantStart - engineStart);
  if (Math.abs(err) <= toleranceSamples) return { nudge: 0, relocate: false };
  if (Math.abs(err) > sampleRate * 0.1) return { nudge: 0, relocate: true };
  return { nudge: err, relocate: false };
}
